if (!customElements.get('size-guide-drawer-opener')) {
  /**
   * @description SizeGuideDrawerOpener is a custom element that opens the size guide drawer.
   */
  class SizeGuideDrawerOpener extends HTMLElement {
    constructor() {
      super();
    }

    /**
     * @description Connected callback for the size guide drawer opener element.
     */
    connectedCallback() {
      this.sizeGuideDrawer = document.getElementById(this.dataset.sizeGuideId);
      this.setEventListeners();
      this.moveSizeGuideDrawerToBody();
    }

    /**
     * @description Sets the event listeners for the size guide drawer opener element.
     */
    setEventListeners() {
      this.addEventListener('click', this.openSizeGuideDrawer.bind(this));
    }

    /**
     * @description Opens the size guide drawer.
     * @param {MouseEvent} event
     */
    openSizeGuideDrawer(event) {
      event?.preventDefault();
      if (!this.sizeGuideDrawer) return;

      this.sizeGuideDrawer.open();

      // Store reference to opener so we can return focus later
      this.sizeGuideDrawer.lastFocusedElement = this;
    }

    /**
     * @description Moves the size guide drawer to the body.
     */
    moveSizeGuideDrawerToBody() {
      // if we are already in the body, we don't need to move it
      if (!this.sizeGuideDrawer || this.sizeGuideDrawer.parentElement === document.body) return;
      this.sizeGuideDrawer.parentElement.removeChild(this.sizeGuideDrawer);
      document.body.appendChild(this.sizeGuideDrawer);
    }
  }
  customElements.define('size-guide-drawer-opener', SizeGuideDrawerOpener);
}

if (!customElements.get('size-guide-drawer')) {
  /**
   * @description SizeGuideDrawer is a custom element for the size guide drawer.
   */
  class SizeGuideDrawer extends HTMLElement {
    /**
     * @description Constructor for the size guide drawer element.
     */
    constructor() {
      super();
      this.handleKeyDown = this.handleKeyDown.bind(this);
    }

    /**
     * @description Connected callback for the size guide drawer element.
     */
    connectedCallback() {
      this.lastFocusedElement = null;

      this.setEventListeners();

      if (!this.hasAttribute('open')) {
        this.removeAttribute('open');
      }

      // Add keyboard event listener
      document.addEventListener('keydown', this.handleKeyDown);
    }

    /**
     * @description Sets the event listeners for the size guide drawer element.
     */
    setEventListeners() {
      this.addEventListener('click', this.handleClick.bind(this));
    }

    /**
     * @description Handles click events.
     * @param {MouseEvent} event - The click event.
     */
    handleClick(event) {
      // If the click is on the close button, close the drawer
      if (event.target.closest('.size-guide-drawer__close')) {
        this.close();
      }
      // if we click the backdrop, close the drawer. The backdrop is a :before pseudo element, so any click not inside of the #size-drawer is the backdrop
      if (!event.target.closest('#size-drawer')) {
        this.close();
      }
    }

    /**
     * @description Handles key down events.
     * @param {KeyboardEvent} event - The key down event.
     */
    handleKeyDown(event) {
      if (event.key === 'Escape' && this.hasAttribute('open')) {
        this.close();
      }
    }

    getScrollLockBreakpoint() {
      if (this.dataset.breakpoint) return this.dataset.breakpoint;
      if (window.matchMedia('(max-width: 749px)').matches) return 'mobile';
      if (window.matchMedia('(max-width: 989px)').matches) return 'tablet';
      return false;
    }

    prefersReducedMotion() {
      return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    }

    /**
     * @description Opens the drawer.
     */
    open() {
      const scrollLockBreakpoint = this.getScrollLockBreakpoint();
      handleBodyScrollLock('lock', scrollLockBreakpoint);
      this.setAttribute('open', '');

      if (this.prefersReducedMotion()) {
        this.classList.add('drawer-opening');
      } else {
        setTimeout(() => {
          this.classList.add('drawer-opening');
        });
      }

      const focusTarget =
        this.querySelector('.size-guide-drawer__close') ||
        this.querySelector('#size-drawer');
      trapFocus(this, focusTarget);
    }

    /**
     * @description Closes the drawer.
     */
    close() {
      if (!this.hasAttribute('open')) return;

      const lastFocusedElement = this.lastFocusedElement;
      const drawer = this.querySelector('.size-drawer');

      const finishClose = () => {
        if (!this.hasAttribute('open')) return;
        this.removeAttribute('open');
        this.classList.remove('drawer-opening');
        handleBodyScrollLock('unlock', this.getScrollLockBreakpoint());

        if (lastFocusedElement) {
          removeTrapFocus(lastFocusedElement);
        } else {
          removeTrapFocus();
        }
      };

      this.classList.remove('drawer-opening');

      if (!drawer || this.prefersReducedMotion()) {
        finishClose();
        return;
      }

      let completed = false;
      const complete = () => {
        if (completed) return;
        completed = true;
        drawer.removeEventListener('transitionend', onTransitionEnd);
        finishClose();
      };

      const onTransitionEnd = (event) => {
        if (event.target === drawer && event.propertyName === 'transform') {
          complete();
        }
      };

      drawer.addEventListener('transitionend', onTransitionEnd);
      setTimeout(complete, 500);
    }

    /**
     * @description Disconnected callback to clean up event listeners.
     */
    disconnectedCallback() {
      document.removeEventListener('keydown', this.handleKeyDown);

      const closeButton = this.querySelector('.size-guide-drawer__close');
      if (closeButton) {
        closeButton.removeEventListener('click', this.close);
      }
    }

    /**
     * @description Getter for the open state.
     */
    get isOpen() {
      return this.hasAttribute('open');
    }
  }

  customElements.define('size-guide-drawer', SizeGuideDrawer);
}

if (!customElements.get('size-guide')) {
  /**
   * @description SizeGuide is a custom element that updates the size guide measurements based on the selected unit.
   */
  class SizeGuide extends HTMLElement {
    /**
     * @description Constructor for the size guide element.
     */
    constructor() {
      super();
    }

    /**
     * @description Connected callback for the size guide element.
     */
    connectedCallback() {
      const radioInputs = this.querySelectorAll('input[name="size-guide-control"]');

      radioInputs.forEach(radio => {
        radio.addEventListener('change', this.handleUnitChange.bind(this));
      });

      const checkedRadio = this.querySelector('input[name="size-guide-control"]:checked');
      if (checkedRadio) {
        this.updateMeasurements(checkedRadio.dataset.sizeUnit);
      }
    }

    /**
     * @description Handles the unit change event.
     * @param {Event} event - The unit change event.
     */
    handleUnitChange(event) {
      const selectedUnit = event.target.dataset.sizeUnit;
      this.updateMeasurements(selectedUnit);
    }

    /**
     * @description Updates the measurements based on the selected unit.
     * @param {string} unit - The selected unit.
     */
    updateMeasurements(unit) {
      if (!unit) return;

      const cells = [...this.querySelectorAll(`[data-${unit}]`)];

      cells.forEach(cell => {
        const val = cell.getAttribute(`data-${unit}`);
        cell.textContent = val;
      });
    }
  }
  customElements.define('size-guide', SizeGuide);
}
